import { CheckIcon, CopyIcon, XIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "../ui/button";
import { useImagineChat } from "@/providers/imagine-chat-provider";
import { imagineStore } from "@/stores/imagine.store";
import { useMemo } from "react";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { errorHandling } from "./error-handling";

export function InChatError({ error }: { error: Error }) {
  const chat = useImagineChat();
  const projectId = imagineStore.use.projectId()!;
  const [copied, copyToClipboard] = useCopyToClipboard();

  const { retriable, title, traceId } = useMemo(() => {
    if (
      error.message &&
      typeof error.message === "string" &&
      error.message.includes("network error")
    ) {
      return {
        title:
          "The connection has timed out. Please refresh the page and try again.",
        retriable: false,
        traceId: undefined,
      };
    }

    return errorHandling.sanitizeChatError(error);
  }, [error]);

  const handleCopytraceId = () => {
    if (!traceId) return;
    copyToClipboard(traceId);
  };

  const handleRetry = async () => {
    if (!retriable) return;
    await chat.regenerate({ projectId });
    chat.resumeStream({ projectId });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex p-4 bg-red-100/30 dark:bg-red-950/30 border gap-3 border-red-300 dark:border-red-900 rounded-lg text-sm"
      >
        <div className="mt-0.5">
          <div className="size-4 flex justify-center text-xs font-semibold items-center rounded-full bg-red-700 text-white dark:bg-red-400 dark:text-black">
            !
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center">
            <div className="font-medium text-red-700 dark:text-red-400">
              An error occurred
            </div>
            <XIcon
              className="size-4 cursor-pointer text-foreground/50"
              onClick={() => chat.clearError()}
            />
          </div>
          <div className="space-y-2">
            <div className="text-foreground/70 break-words overflow-wrap-anywhere">
              {title}
            </div>
            {(retriable || traceId) && (
              <div className="flex items-center justify-between">
                <div>
                  {retriable && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleRetry}
                      className="cursor-pointer px-0 py-0 text-primary/70"
                    >
                      Retry
                    </Button>
                  )}
                </div>
                <div>
                  {traceId && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleCopytraceId}
                      className="cursor-pointer px-0 py-0 gap-1.5 text-primary/70"
                    >
                      {copied ? (
                        <CheckIcon className="size-3.5" />
                      ) : (
                        <CopyIcon className="size-3.5" />
                      )}
                      Copy Request ID
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
